import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, LogOut, Eye, Twitter, Instagram, Facebook, Linkedin } from "lucide-react";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { ForcefieldLogo } from "@/components/ForcefieldLogo";

// Platform icon mapping
const PLATFORM_ICONS = {
  twitter: Twitter,
  instagram: Instagram,
  facebook: Facebook,
  linkedin: Linkedin
};

export default function HistoryPage() {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API}/analysis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch history");
      }
      
      const data = await response.json();
      setAnalyses(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleDelete = useCallback(async () => {
    if (!deleteId) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API}/analysis/${deleteId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error("Failed to delete analysis");
      }
      
      toast.success("Analysis deleted");
      setAnalyses(prev => prev.filter(a => a.id !== deleteId));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  }, [deleteId, token]);

  const getRiskColor = useCallback((level) => {
    const levelLower = level.toLowerCase();
    const colors = {
      high: "bg-red-500/10 text-red-400 border-red-500/20",
      medium: "bg-amber-500/10 text-amber-400 border-amber-500/20",
      low: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
    };
    return colors[levelLower] || "bg-gray-500/10 text-gray-400 border-gray-500/20";
  }, []);

  const formatDate = useCallback((dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  }, []);

  const getHandlesSummary = useCallback((handles) => {
    const platforms = [];
    Object.entries(handles).forEach(([key, value]) => {
      if (value && PLATFORM_ICONS[key]) {
        platforms.push({ icon: PLATFORM_ICONS[key], name: value, key });
      }
    });
    return platforms;
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDeleteId(null);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="history-logo-link">
            <ForcefieldLogo className="w-10 h-10" />
            <span className="font-outfit font-bold text-xl text-white tracking-tight">Social <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">Forcefield</span></span>
          </Link>
          <nav className="flex items-center gap-4">
            <Link to="/analyze">
              <Button variant="ghost" className="text-gray-400 hover:text-white gap-2" data-testid="history-new-analysis-btn">
                <Plus className="w-4 h-4" />
                New Scan
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              onClick={logout}
              className="text-gray-400 hover:text-white gap-2"
              data-testid="history-logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </nav>
        </div>
      </header>

      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-1/4 w-[400px] h-[400px] bg-violet-600/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-1/4 w-[300px] h-[300px] bg-fuchsia-600/5 rounded-full blur-[100px]" />
      </div>

      {/* Main content */}
      <main className="pt-28 pb-16 px-6 relative">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-sm text-violet-400 uppercase tracking-widest mb-2">/scan history/</p>
              <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">Past Scans</h1>
            </div>
            <Link to="/analyze">
              <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white gap-2 rounded-full" data-testid="create-analysis-btn">
                <Plus className="w-4 h-4" />
                New Scan
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-violet-500/20 border-t-violet-500 rounded-full animate-spin" />
            </div>
          ) : analyses.length === 0 ? (
            <div className="p-12 rounded-2xl bg-white/[0.02] border border-white/5 text-center">
              <div className="mx-auto mb-4">
                <ForcefieldLogo className="w-20 h-20 mx-auto opacity-50" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-white mb-2">No scans yet</h3>
              <p className="text-gray-400 mb-6">Activate your first forcefield scan to see results here</p>
              <Link to="/analyze">
                <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white rounded-full" data-testid="empty-create-analysis-btn">
                  Start First Scan
                </Button>
              </Link>
            </div>
          ) : (
            <div className="rounded-2xl bg-white/[0.02] border border-white/5 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Date</TableHead>
                    <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Handles</TableHead>
                    <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Risk Score</TableHead>
                    <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Risk Level</TableHead>
                    <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {analyses.map((analysis) => (
                    <TableRow 
                      key={analysis.id} 
                      className="border-white/5 hover:bg-white/[0.02] transition-colors"
                      data-testid={`history-row-${analysis.id}`}
                    >
                      <TableCell className="text-gray-300 font-mono text-sm">
                        {formatDate(analysis.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 flex-wrap">
                          {getHandlesSummary(analysis.handles).map((p) => (
                            <div key={p.key} className="flex items-center gap-1 text-gray-400">
                              <p.icon className="w-3 h-3" />
                              <span className="text-xs font-mono">{p.name}</span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-white">{analysis.overall_risk_score}</span>
                        <span className="text-gray-500">/100</span>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getRiskColor(analysis.overall_risk_level)} border uppercase text-xs`}>
                          {analysis.overall_risk_level}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/dashboard/${analysis.id}`)}
                            className="text-gray-400 hover:text-white"
                            data-testid={`view-analysis-${analysis.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteId(analysis.id)}
                            className="text-gray-400 hover:text-red-400"
                            data-testid={`delete-analysis-${analysis.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Powered by */}
          <p className="mt-12 text-center text-gray-600 text-sm">
            powered by <a href="https://agentseven.ai" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">AgentSeven.ai</a>
          </p>
        </div>
      </main>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={handleCloseDialog}>
        <AlertDialogContent className="bg-[#121212] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Scan</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Are you sure you want to delete this scan? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-transparent border-white/20 text-white hover:bg-white/5"
              data-testid="cancel-delete-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
